import { createContext, useContext, useEffect, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function initAuth() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!cancelled && session?.user) {
          setUser(session.user);
        }
      } catch {
        // Session check failed, user stays null
      }
      if (!cancelled) {
        setLoading(false);
      }
    }

    initAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<{ error: string | null }> => {
    const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL as string;
    const anonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY as string;

    // Step 1: Try direct browser sign-in first (fast path for existing users with correct password)
    const { data: directData, error: directErr } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!directErr && directData.session) {
      setUser(directData.session.user);
      return { error: null };
    }

    // Step 2: Direct sign-in failed — call edge function to provision/update user
    let provisionError: string | null = null;
    try {
      const res = await fetch(`${supabaseUrl}/functions/v1/admin-auth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${anonKey}`,
        },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!data.success) {
        provisionError = data.error || '认证失败';
      }
    } catch {
      provisionError = '登录服务暂时不可用，请稍后重试';
    }

    if (provisionError) {
      return { error: provisionError };
    }

    // Step 3: Edge function succeeded — wait a moment for propagation, then retry
    await new Promise((r) => setTimeout(r, 800));

    const { data: retryData, error: retryErr } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (!retryErr && retryData.session) {
      setUser(retryData.session.user);
      return { error: null };
    }

    // If we still can't sign in, the password might be wrong
    // (user exists but edge function couldn't update password, or wrong credentials)
    return { error: '登录失败，密码错误。请确认密码后重试。' };
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // non-critical
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}